
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Controller, useFormContext } from 'react-hook-form';

import { SectionDefinition } from '@/features/organizations';
import { useEntitySettings } from '@/features/shared/hooks/use-entity-settings';
import { useResponsiveValue } from '@/hooks/use-responsive-value';


interface CustomFieldsSectionProps {
  novelId: string;
  sections?: SectionDefinition[]; // Optional: if provided, won't fetch from Firestore
}

export function CustomFieldsSection({ sections: providedSections }: CustomFieldsSectionProps) {
  const { control, register, formState: { errors } } = useFormContext();
  const resolveResponsive = useResponsiveValue();
  
  // Use the canonical hook to get settings from denormalized novel data
  const { sections: hookSections, isLoading } = useEntitySettings('organizations');
  
  const sections = providedSections || hookSections;

  if (isLoading && !providedSections) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!sections || sections.length === 0) return null;

  return (
    <Stack spacing={4}>
      {sections.map((section) => (
        <Box key={section.id} sx={{ pt: 2 }}>
          <Typography variant="h2" sx={{ mb: 2 }}>
            {section.title}
          </Typography>
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Grid container spacing={3}>
              {section.fields.map((field) => (
                <Grid key={field.id} size={resolveResponsive({ xs: 12, md: field.type === 'textarea' ? 12 : 6 })}>
                  {field.type === 'select' ? (
                    <FormControl fullWidth variant="filled" size="small">
                      <InputLabel shrink>{field.label}</InputLabel>
                      <Controller
                        name={field.id}
                        control={control}
                        defaultValue=""
                        render={({ field: formField }) => (
                          <Select
                            {...formField}
                            label={field.label}
                            displayEmpty
                            error={!!errors[field.id]}
                            sx={{ borderRadius: 1.5, '&:before, &:after': { display: 'none' } }}
                          >
                            <MenuItem value="" disabled>
                              <Typography variant="body2" color="text.disabled">
                                {field.placeholder || 'Select...'}
                              </Typography>
                            </MenuItem>
                            {(field.options || []).map((opt) => (
                              <MenuItem key={opt} value={opt}>
                                {opt}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                      {errors[field.id] && <FormHelperText error>{errors[field.id]?.message as string}</FormHelperText>}
                    </FormControl>
                  ) : (
                    <TextField
                      {...register(field.id)}
                      label={field.label}
                      placeholder={field.placeholder}
                      fullWidth
                      multiline={field.type === 'textarea'}
                      minRows={field.type === 'textarea' ? 4 : 1}
                      variant="filled"
                      size="small"
                      type={field.type === 'number' ? 'number' : 'text'}
                      error={!!errors[field.id]}
                      helperText={errors[field.id]?.message as string}
                      InputProps={{
                        disableUnderline: true,
                        sx: {
                          borderRadius: 1.5,
                          px: 1.5,
                          py: field.type === 'textarea' ? 1.5 : 0,
                          '& textarea': field.type === 'textarea' ? { resize: 'vertical' } : {}
                        }
                      }}
                      InputLabelProps={{ shrink: true }}
                      sx={{
                        '& .MuiFilledInput-root': {
                          minHeight: field.type === 'textarea' ? 'auto' : 44,
                        }
                      }}
                    />
                  )}
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Box>
      ))}
    </Stack>
  );
}
